
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import type { SettingsTabProps } from './types';

const AdsSettingsTab: React.FC<SettingsTabProps> = ({ settings, onSettingsChange }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsToUpdate: Record<string, any>) => {
      console.log('Updating ads settings:', settingsToUpdate);
      const updatePromises = Object.entries(settingsToUpdate).map(async ([key, value]) => {
        const jsonValue: Json = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null
          ? value
          : JSON.stringify(value);
          
        const { error } = await supabase
          .from('website_settings')
          .upsert({ 
            key, 
            value: jsonValue, 
            description: `Setting for ${key}` 
          }, {
            onConflict: 'key'
          });
          
        if (error) {
          throw error;
        }
      });
      
      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      console.log('Ads settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['website-settings'] });
      toast.success('Ads settings updated successfully');
      setErrors({});
    },
    onError: (error: any) => {
      console.error('Failed to update ads settings:', error);
      toast.error('Failed to update ads settings');
      setErrors({ ads: error.message || 'Failed to save settings' });
    },
  });

  const validateSettings = () => {
    const newErrors: Record<string, string> = {};
    
    if (settings.native_ad_frequency && (settings.native_ad_frequency < 1 || settings.native_ad_frequency > 50)) {
      newErrors.native_ad_frequency = 'Ad frequency must be between 1 and 50';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateSettings()) {
      toast.error('Please fix the validation errors before saving');
      return;
    }

    const adsSettings = {
      native_ad_frequency: settings.native_ad_frequency,
    };

    try {
      await updateSettingsMutation.mutateAsync(adsSettings);
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ads Settings</CardTitle>
        <CardDescription>Configure advertisement display settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {errors.ads && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {errors.ads}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="native_ad_frequency">Native Ad Frequency</Label>
          <Input
            id="native_ad_frequency"
            type="number"
            min="1"
            max="50"
            value={settings.native_ad_frequency || 8}
            onChange={(e) => onSettingsChange('native_ad_frequency', parseInt(e.target.value) || 8)}
            className={errors.native_ad_frequency ? 'border-red-500' : ''}
          />
          {errors.native_ad_frequency && (
            <p className="text-sm text-red-600">{errors.native_ad_frequency}</p>
          )}
          <p className="text-sm text-gray-500">
            Show native ads every N games in listings (1-50)
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Ad Management</h4>
          <p className="text-blue-700 text-sm mb-3">
            For detailed ad management, including creating, editing, and managing ad placements, 
            please visit the <strong>Ads</strong> section in the main admin navigation.
          </p>
          <div className="text-blue-700 text-sm">
            <p className="font-medium mb-1">Available Ad Positions:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Header & Footer - Site-wide placement</li>
              <li>Sidebar - Side panel ads</li>
              <li>Before/After Game - Around game content</li>
              <li>Between Games - Native ads in game listings</li>
              <li>Blog Above Post - Above blog post content</li>
              <li>Blog Related Games - Native ads in related games section</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleSave} 
            disabled={updateSettingsMutation.isPending}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {updateSettingsMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {updateSettingsMutation.isPending ? 'Saving...' : 'Save Ads Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdsSettingsTab;
